import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '../../../common/index';
import { AppContext } from '../../../common/contexts/AppContext';
import { ClientCustomerEntity, ClientProjectEntity, ClientRemoteServiceEntity, IContentSyncApi } from '@edgebox/api-rest-client';
import { SystemEnvironment, SystemOrganization, SystemSpace } from './PublisherClient';
import { ContentCloudPermission, ContentCloudService } from './shared-permissions';

export interface ContentCloudComponentData {
  contentCloud: ClientRemoteServiceEntity;
  project: ClientProjectEntity;

  organization: SystemOrganization;
  space: SystemSpace;
  environment: SystemEnvironment;

  accessToken: string;

  featureConfig: Record<string, any>;
}

export interface ContentCloudComponentProps {
  contentCloudData?: ContentCloudComponentData;
}

const RETRY_INTERVAL_MS = 5_000;
const ALL_SERVICES = [
  ContentCloudService.ASSETS,
  ContentCloudService.ASSET_PREVIEWS,
  ContentCloudService.CDN,
  ContentCloudService.DEV,
  ContentCloudService.LIVE,
  ContentCloudService.PREVIEW,
  ContentCloudService.PUBLISHER,
];

export async function updateAccessToken(
  api: IContentSyncApi,
  { environment, contentCloud, project }: ContentCloudComponentData,
  permissions: ContentCloudPermission[],
  services?: ContentCloudService[]
) {
  return await api.syndication.remoteServices.getContentCloudRequestToken(
    contentCloud.id,
    project.id,
    { permissions, services: services ?? ALL_SERVICES },
    {
      environmentIds: [environment.id],
    }
  );
}

export type ContentCloudComponentDataLoader = {
  loaded: boolean;
  aborted: boolean;
  contentCloudData?: ContentCloudComponentData;
  getContentCloudData: Promise<ContentCloudComponentData>;
  abort: () => void;
  onLoad: (clb: (contentCloudData: ContentCloudComponentData) => void) => void;
};
export function initContentCloudAndRetry(
  api: IContentSyncApi,
  contentCloud: ClientRemoteServiceEntity,
  permissions: ContentCloudPermission[],
  project: ClientProjectEntity,
  services?: ContentCloudService[]
): ContentCloudComponentDataLoader {
  const callbacks: ((contentCloudData: ContentCloudComponentData) => void)[] = [];
  const result: ContentCloudComponentDataLoader = {
    loaded: false,
    aborted: false,
    getContentCloudData: new Promise<ContentCloudComponentData>(async (resolve) => {
      let data: ContentCloudComponentData | undefined;
      let aborted = false;

      do {
        data = await initContentCloud(api, contentCloud, permissions, project, services);

        if (aborted) {
          return;
        }

        if (!data) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL_MS));
        }
      } while (!data);

      resolve(data);

      for (const clb of callbacks) {
        clb(data);
      }
    }),
    abort: () => {
      result.aborted = true;
    },
    onLoad: (clb) => {
      callbacks.push(clb);
    },
  };

  return result;
}

export async function initContentCloud(
  api: IContentSyncApi,
  contentCloud: ClientRemoteServiceEntity,
  permissions: ContentCloudPermission[],
  project: ClientProjectEntity,
  services?: ContentCloudService[]
): Promise<ContentCloudComponentData | undefined> {
  const {
    jwt,
    organization: organizationRaw,
    space: spaceRaw,
  } = await api.syndication.remoteServices.getContentCloudSpace(contentCloud.id, project.id, {
    permissions,
    services: services ?? ALL_SERVICES,
  });

  const organization: SystemOrganization = organizationRaw;
  const space: SystemSpace = spaceRaw;

  const environment = space.defaultEnvironment;

  return {
    contentCloud,
    project,

    organization,
    space,
    environment,

    accessToken: jwt,

    featureConfig: {
      ...(organization.featureConfig ?? {}),
      ...(space.featureConfig ?? {}),
      ...(environment.featureConfig ?? {}),
    },
  };
}

export function WithContentCloud(props: {
  children: (data?: ContentCloudComponentData) => React.ReactNode;
  permissions: ContentCloudPermission[];
  services?: ContentCloudService[];
  project?: ClientProjectEntity;
}) {
  const api = useContext(ApiContext);
  const appContext = useContext(AppContext);

  let { customer, project } = appContext;
  if (props.project) {
    project = props.project;
  }

  const [contentCloudData, setContentCloudData] = useState<ContentCloudComponentDataLoader | undefined>();

  const permissionString = props.permissions.sort().join(',');
  const serviceString = props.services?.sort().join(',') ?? null;
  useEffect(() => {
    if (!customer || !project || !api || !project.syncCore) {
      return;
    }

    project.syncCore?.get().then(async (contentCloud) => {
      if (!contentCloud) {
        return;
      }

      const data = await initContentCloudAndRetry(
        api,
        contentCloud,
        permissionString.split(',') as any,
        project,
        serviceString?.split(',') as any
      );
      setContentCloudData(data);

      data.onLoad((contentCloudData) => {
        setContentCloudData({
          ...data,
          contentCloudData,
        });
      });
    });
  }, [api, customer, project, permissionString, serviceString]);

  return props.children(contentCloudData?.contentCloudData);
}

export function withContentCloud<P extends ContentCloudComponentProps>(
  Component: React.FunctionComponent<P>,
  permissions: ContentCloudPermission[],
  services?: ContentCloudService[]
): React.FunctionComponent<Omit<P, keyof ContentCloudComponentProps>> {
  return function ComponentWithContentCloud(otherProps: Omit<P, keyof ContentCloudComponentProps>) {
    return (
      <WithContentCloud permissions={permissions} services={services}>
        {(data?: ContentCloudComponentData) => (<Component {...({ ...otherProps, contentCloudData: data } as P)} />) as React.ReactNode}
      </WithContentCloud>
    );
  };
}
